import { useState } from 'react';
import { IconCheck, IconX } from '@tabler/icons-react';
import {
    Combobox,
    Group,
    Pill,
    PillsInput,
    useCombobox,
    InputWrapper,
    InputWrapperProps,
    ActionIcon,
    Tooltip,
} from '@mantine/core';

export type MultiSelectOption = {
    label: string;
    value: string;
};

type MultiSelectCreatableProps = {
    // initial options available
    options: MultiSelectOption[];
    // currently selected options (the form field’s value)
    value: MultiSelectOption[];
    // called when the selection changes (the form field’s onChange)
    onChange: (value: MultiSelectOption[]) => void;
} & Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange' | 'value'> &
    Pick<InputWrapperProps, 'label' | 'error'> & {
        // placeholder for the search input
        placeholder?: string;
        // if false, disables the ability to create new entries (defaults to true)
        creatable?: boolean;
    };

export function MultiSelectCreatable({
    options: initialOptions,
    value,
    onChange,
    label,
    error,
    placeholder = 'Search values',
    creatable = true,
    ...others
}: MultiSelectCreatableProps) {
    // Use Mantine's combobox store to help manage keyboard navigation, etc.
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
    });

    // We'll keep our available options in state (so we can add new options)
    const [data, setData] = useState<MultiSelectOption[]>(initialOptions);
    // And we manage the current search input separately
    const [search, setSearch] = useState('');

    // Check if there is an exact match for the search text (ignoring case)
    const exactOptionMatch = data.some(
        (item) => item.label.toLowerCase() === search.trim().toLowerCase()
    );

    // When an option is selected (or the create option is chosen)
    const handleValueSelect = (selectedValue: string) => {
        // Clear the search field after selection
        setSearch('');
        if (selectedValue === '$create') {
            // Only allow creating new entries if creatable is true
            if (!creatable) return;
            // Create a new option using the current search text
            const newOption = { label: search, value: search };
            setData((current) => [...current, newOption]);
            onChange([...value, newOption]);
        } else {
            // Toggle selection for the chosen option
            const alreadySelected = value.some((item) => item.value === selectedValue);
            if (alreadySelected) {
                onChange(value.filter((item) => item.value !== selectedValue));
            } else {
                const option = data.find((item) => item.value === selectedValue);
                if (option) {
                    onChange([...value, option]);
                }
            }
        }
        combobox.closeDropdown();
    };

    // Remove an option from the current value
    const handleValueRemove = (removedValue: string) =>
        onChange(value.filter((item) => item.value !== removedValue));

    // Handler to clear all selected values
    const handleClearAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onChange([]);
    };

    // Handler for the "Select All" action.
    // This sets the selection to include all available options.
    const handleSelectAll = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onChange(data);
    };

    // Render pills for each selected value
    const selectedPills = value.map((item) => (
        <Pill key={item.value} withRemoveButton onRemove={() => handleValueRemove(item.value)}>
            {item.label}
        </Pill>
    ));

    // Build the list of options to display in the dropdown,
    // filtering by the current search text
    const renderedOptions = data
        .filter((item) => item.label.toLowerCase().includes(search.trim().toLowerCase()))
        .map((item) => (
            <Combobox.Option
                value={item.value}
                key={item.value}
                active={value.some((v) => v.value === item.value)}
            >
                <Group gap="sm">
                    {value.some((v) => v.value === item.value) ? <IconCheck size={12} /> : null}
                    <span>{item.label}</span>
                </Group>
            </Combobox.Option>
        ));

    return (
        <div {...others}>
            <InputWrapper label={label} error={error}>
                <Combobox store={combobox} onOptionSubmit={handleValueSelect} withinPortal={true}>
                    <Combobox.DropdownTarget>
                        {/* Wrap the PillsInput in a relative container to place action icons */}
                        <div style={{ position: 'relative' }}>
                            {/* Give extra right padding so the text does not run under the icons */}
                            <PillsInput onClick={() => combobox.openDropdown()} style={{ paddingRight: 80 }}>
                                <Pill.Group>
                                    {selectedPills}
                                    <Combobox.EventsTarget>
                                        <PillsInput.Field
                                            onFocus={() => combobox.openDropdown()}
                                            onBlur={() => combobox.closeDropdown()}
                                            value={search}
                                            placeholder={placeholder}
                                            onChange={(event) => {
                                                combobox.updateSelectedOptionIndex();
                                                setSearch(event.currentTarget.value);
                                            }}
                                            onKeyDown={(event) => {
                                                // If Backspace is pressed with an empty search field, remove the last selected option
                                                if (event.key === 'Backspace' && search.length === 0 && value.length > 0) {
                                                    event.preventDefault();
                                                    handleValueRemove(value[value.length - 1].value);
                                                }
                                            }}
                                        />
                                    </Combobox.EventsTarget>
                                </Pill.Group>
                            </PillsInput>
                            {/* Right-hand side action icons */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: 8,
                                    transform: 'translateY(-50%)',
                                    display: 'flex',
                                    gap: 4,
                                }}
                            >
                                {value.length > 0 && (
                                    <Tooltip withArrow label={"Clear All"}>
                                        <ActionIcon onClick={handleClearAll} title="Clear All">
                                            <IconX size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                                {data.length !== value.length && (
                                    <Tooltip withArrow label={"Select All"}>
                                        <ActionIcon onClick={handleSelectAll} title="Select All">
                                            <IconCheck size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </Combobox.DropdownTarget>

                    <Combobox.Dropdown>
                        <Combobox.Options>
                            {renderedOptions}
                            {/* Show the create option only when creatable is true */}
                            {creatable && !exactOptionMatch && search.trim().length > 0 && (
                                <Combobox.Option value="$create">+ Create {search}</Combobox.Option>
                            )}
                            {exactOptionMatch &&
                                search.trim().length > 0 &&
                                renderedOptions.length === 0 && <Combobox.Empty>Nothing found</Combobox.Empty>}
                        </Combobox.Options>
                    </Combobox.Dropdown>
                </Combobox>
            </InputWrapper>
        </div>
    );
}
